// Boot, fixed-timestep loop, viewport fitting, UI wiring.

import { initWorld, type BiomeId } from './world';
import { LAND_HUD_LABEL, LAND_ORDER, unlockedLands } from './lands';
import {
  Game, type ActiveSystemItem, type CrashPresentation, type PerkBuildEntry, type PerkDef, type SpecialReserveItem,
} from './game';
import { consumeSpecialPress, initInput, input, releaseAllInput } from './input';
import { initMenuNav } from './menunav';
import { initGamepad, pollGamepad } from './gamepad';
import * as hud from './hud';
import {
  unlockAudio, preloadVoiceClip, preloadVoiceClipAsync, startMusic, startAmbience, setMusicTrack, primeMusicTrack,
  suspendAudio, resumeAudio, playVoiceClip, VOICE, nudgeMusic,
  setAmbientBiome, ambientState, sfxScoreRecord, playAnimalCall, sfxCowMoo,
  BIOME_TRACK,
} from './audio';
import type { BoardRow, SeasonRow, RunResult } from './scoring';
import { getGuestName, getIdentityMode, setGuestName } from './identity-local';
import { currentAuth, loadAuth } from './auth-loader';
import { seasonInfo } from './season';
import { WRITE_RELAYS } from './relays';
import { awardState } from './achievements';
import { congregationState, gatheredCount } from './congregation';
import { ROSTER } from './roster';
import { AdaptiveResolution, mobileResolutionSteps } from './quality';
import type { DailyMetric, DailyView } from './daily';
declare const __BUILD_ID__: string;
declare const __APP_VERSION__: string;

const canvas = document.getElementById('game') as HTMLCanvasElement;
const world = initWorld(canvas);

const overlay = {
  menu: document.getElementById('menu')!,
  gameover: document.getElementById('gameover')!,
  board: document.getElementById('board')!,
};
const crashCallout = document.getElementById('crash-callout')!;
const crashKicker = document.getElementById('crash-kicker')!;
const crashStatus = document.getElementById('crash-status')!;
const touchControls = document.getElementById('touch')!;
const pauseButton = document.getElementById('pause-btn') as HTMLButtonElement;

function presentCrash(state: CrashPresentation): void {
  const active = state !== null;
  if (active) document.body.dataset.crash = state;
  else delete document.body.dataset.crash;
  touchControls.inert = active;
  if (active) touchControls.setAttribute('aria-hidden', 'true');
  else touchControls.removeAttribute('aria-hidden');
  pauseButton.disabled = active;
  crashCallout.hidden = !active;
  if (!active) return;
  if (state === 'falling') releaseAllInput();
  crashKicker.textContent = state === 'impact' ? 'SAUCER DOWN' : 'ENERGY DEPLETED';
  crashStatus.textContent = state === 'impact' ? 'IMPACT CONFIRMED' : 'BRACE FOR IMPACT';
}

let lastRun: RunResult | null = null;
let flightReceipt = '';
let flightCardFile: File | null = null;
let flightCardSerial = 0;
let scoreChase: { reset(score?: number): void; update(score: number): void } | null = null;
let perkUi: typeof import('./perks') | null = null;
let perkUiReady: Promise<typeof import('./perks')> | null = null;
let perkOfferSerial = 0;
let specialReserve: SpecialReserveItem[] = [];
let specialReserveSerial = 0;
let activeSystems: ActiveSystemItem[] = [];
let activeSystemsSerial = 0;
let installedBuildSerial = 0;
// Optional daily pressure joins only after the pilot has cleared the opening
// wave. Progress records from launch, but the first cow should hand cleanly to
// the nearby leader rather than opening another instruction at the same time.
let dailyHudArmed = false;
let dailyRuntime: typeof import('./daily') | null = null;
const dailyReady = import('./daily').then((runtime) => {
  dailyRuntime = runtime;
  renderDaily(runtime.dailyView());
  return runtime;
});
let scoringReady: Promise<typeof import('./scoring')> | null = null;
let scoringAttempt = 0;
const scoringLoaders: Array<() => Promise<typeof import('./scoring')>> = [
  () => import('./scoring'),
  // @ts-expect-error Vite query creates a cache-distinct production module.
  () => import('./scoring?retry=1') as Promise<typeof import('./scoring')>,
];

/** Boards and score claims are important, but neither belongs on the blocking
 * flight entry path. One shared retryable loader serves the menu, outbox and
 * debrief so a transient chunk failure never permanently disables publishing. */
function loadScoring(): Promise<typeof import('./scoring')> {
  if (scoringReady) return scoringReady;
  const loader = scoringLoaders[Math.min(scoringAttempt++, scoringLoaders.length - 1)];
  scoringReady = loader().catch((error) => {
    scoringReady = null;
    throw error;
  });
  return scoringReady;
}

function loadPerkUi(): Promise<typeof import('./perks')> {
  if (perkUi) return Promise.resolve(perkUi);
  return perkUiReady ??= import('./perks').then((module) => {
    perkUi = module;
    return module;
  }, (error) => {
    perkUiReady = null;
    throw error;
  });
}

function formatDailyValue(metric: DailyMetric, value: number): string {
  if (metric === 'score' || metric === 'crop') return value.toLocaleString('en-GB');
  if (metric === 'combo') return `x${value}`;
  return String(value);
}

function renderDaily(view?: DailyView): void {
  if (!view) {
    if (dailyRuntime) view = dailyRuntime.dailyView();
    else { void dailyReady; return; }
  }
  const { contract } = view;
  const card = document.getElementById('daily-contract')!;
  card.dataset.complete = view.completed ? '1' : '';
  document.getElementById('daily-title')!.textContent = contract.title;
  document.getElementById('daily-desc')!.textContent = contract.description;
  document.getElementById('daily-streak')!.textContent = view.streak > 0 ? `🔥 ${view.streak}` : '';
  const best = formatDailyValue(contract.metric, Math.min(view.best, contract.target));
  const target = formatDailyValue(contract.metric, contract.target);
  const reward = contract.reward.toLocaleString('en-GB');
  document.getElementById('daily-progress')!.textContent = view.completed
    ? `SIGNAL LOCKED · +${reward}`
    : `${view.best > 0 ? `BEST ${best}` : `TARGET ${target}`} · REWARD +${reward}`;

  const dailyHud = document.getElementById('daily-hud')!;
  const mobileDaily = document.getElementById('mobile-daily')!;
  dailyHud.hidden = !dailyHudArmed;
  mobileDaily.hidden = !dailyHudArmed;
  dailyHud.dataset.complete = view.completed ? '1' : '';
  mobileDaily.dataset.complete = view.completed ? '1' : '';
  dailyHud.textContent = view.completed
    ? `📡 DAILY SIGNAL LOCKED ✓ · ${view.streak} DAY${view.streak === 1 ? '' : 'S'}`
    : `📡 DAILY ${contract.hudLabel} ${formatDailyValue(contract.metric, view.current)}/${target}`;
  mobileDaily.textContent = view.completed
    ? `DAILY LOCKED ✓ · ${view.streak}D`
    : `DAILY ${contract.hudLabel} ${formatDailyValue(contract.metric, view.current)}/${target}`;
}

let game: Game;
let tourHudArmed = false;
const coarse = matchMedia('(pointer: coarse)').matches;
const motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
const renderQuality = new AdaptiveResolution(
  coarse ? mobileResolutionSteps(window.devicePixelRatio || 1) : [world.renderer.getPixelRatio()],
);
let beamControl = coarse ? 'BEAM' : 'SPACE';

function startDailyFlight(view: DailyView): void {
  game?.setDailyStreak(view.streak);
  renderDaily(view);
}

function updateDailyMetric(metric: DailyMetric, value: number): void {
  if (!game || game.demoMode) return;
  if (!dailyRuntime) {
    void dailyReady.then(() => updateDailyMetric(metric, value));
    return;
  }
  const update = dailyRuntime.recordDailyProgress(metric, value);
  renderDaily(update.view);
  if (update.justCompleted) {
    game.completeDailyContract(update.view.streak, update.view.contract.reward);
  }
}

game = new Game(world, {
  onScore: (score) => {
    if (score === 0) {
      dailyHudArmed = false;
      tourHudArmed = false;
      hud.setTour(game.stage, LAND_HUD_LABEL[world.biome], false);
    }
    hud.setScore(score);
    if (!game || game.demoMode) return;
    scoreChase?.update(score);
    if (score === 0) {
      // The streak has to be in hand before the Wave 2 draft, which is the
      // first thing it pays out on.
      if (dailyRuntime) startDailyFlight(dailyRuntime.startDailyRun());
      else void dailyReady.then((runtime) => startDailyFlight(runtime.startDailyRun()));
    }
    updateDailyMetric('score', score);
  },
  onCows: (cows) => updateDailyMetric('cows', cows),
  onObjective: (metric, value) => updateDailyMetric(metric, value),
  onCombo: (mult, active, fraction, need, nextMult) => {
    hud.setCombo(mult, active, fraction, need, nextMult);
    updateDailyMetric('combo', mult);
  },
  onWave: (wave) => {
    hud.setWave(wave);
    // The Wave 2 draft is the natural end of the opening lesson. Reveal the
    // optional contract when play resumes; its Wave 1 progress was retained.
    if (wave >= 2 && !game.demoMode) dailyHudArmed = true;
    updateDailyMetric('wave', wave);
  },
  onWaveMastery: hud.setWaveMastery,
  onHull: hud.setHull,
  onDamage: hud.damage,
  onQuota: (done, quota, council, councilSize) => {
    // The route has meaning once the player has written their first leader to
    // the Council. Animal score alone no longer makes two HUD systems appear
    // beneath the first-run instruction and FIRST CONTACT badge.
    if (council > 0 && !tourHudArmed && !game.demoMode) tourHudArmed = true;
    hud.setQuota(done, quota, council, councilSize);
    hud.setTour(game.stage, LAND_HUD_LABEL[world.biome], tourHudArmed);
    updateDailyMetric('council', council);
  },
  onToast: hud.toastMember,
  onBanner: hud.banner,
  // Unlike event toasts, the one indispensable first action names the current
  // control and stays up long enough for a new player to read and try it.
  onBeamHint: (animal) => hud.banner(`HOLD ${beamControl} OVER THE NEARBY ${animal}`, 4200),
  onLeaderHint: () => hud.banner(`NEXT: HOLD ${beamControl} OVER THE ORANGE-GLOWING LEADER`, 4200),
  onCarry: (text) => {
    if (text === 'NEXT LEADER' && !document.getElementById('toast')!.hidden) return;
    if (text) hud.hideBanner();
    hud.setCarry(text);
  },
  onCarryNavigation: hud.setCarryNavigation,
  onThreat: hud.setThreat,
  onPerkOffer: presentPerks,
  onBuild: presentBuild,
  onPowerups: presentPowerups,
  onSpecial: presentSpecial,
  onFever: hud.setFever,
  onBoss: hud.setBoss,
  onAward: hud.awardToast,
  onFlash: hud.flash,
  onBiome: hud.showBiomeCard,
  onRitual: (active) => {
    document.body.dataset.ritual = active ? '1' : '';
  },
  onCrash: presentCrash,
  onGameOver: () => {
    if (game.demoMode) exitDemo();
    else {
      setGameplayControls(false);
      // The frozen world remains the backdrop, but the report owns the words now.
      crashCallout.hidden = true;
      setTimeout(showGameOver, game.extracted ? 180 : 700);
    }
  },
});
game.setReducedMotion(motionPreference.matches);
motionPreference.addEventListener('change', (event) => game.setReducedMotion(event.matches));

function setGameplayControls(show: boolean): void {
  if (show) document.body.dataset.gameplay = '1';
  else delete document.body.dataset.gameplay;
}

/* ---------------- starting land ---------------- */
// The tour owns biome progression: every public run opens in the valley and
// earns later lands in order. ?biome=<id> remains a development-only pin.

function forcedBiome(): BiomeId | null {
  const q = new URLSearchParams(location.search).get('biome');
  return LAND_ORDER.includes(q as BiomeId) ? q as BiomeId : null;
}

/** Where the next run opens: an explicit dev pin, otherwise the valley. */
function startingLand(): BiomeId {
  return forcedBiome() ?? 'valley';
}

// The menu backdrop previews the land the next run will open in.
world.applyBiome(startingLand());
setAmbientBiome(world.biome);

/* ---------------- perk draft overlay ---------------- */

function presentPerks(perks: PerkDef[], canDecline: boolean, clearBonus = 0, streak = 0): void {
  const serial = ++perkOfferSerial;
  document.getElementById('perk')!.hidden = false;
  setGameplayControls(false);
  if (perkUi) {
    perkUi.showPerks(game, setGameplayControls, perks, canDecline, clearBonus, streak);
    return;
  }
  void loadPerkUi().then((module) => {
    if (serial !== perkOfferSerial) return;
    module.showPerks(game, setGameplayControls, perks, canDecline, clearBonus, streak);
  }, () => {
    if (serial !== perkOfferSerial) return;
    document.getElementById('perk-kicker')!.textContent = 'MOTHERSHIP SIGNAL LOST · RETRYING';
    setTimeout(() => presentPerks(perks, canDecline, clearBonus, streak), 500);
  });
}

function presentSpecial(items: SpecialReserveItem[]): void {
  specialReserve = items;
  const serial = ++specialReserveSerial;
  if (perkUi) {
    perkUi.renderSpecialHud(items);
    return;
  }
  // Empty is already the authored first-paint state. A real stored payload
  // arrives well after startRun has begun prewarming this deferred renderer.
  if (items.length === 0) return;
  void loadPerkUi().then((module) => {
    if (serial === specialReserveSerial) module.renderSpecialHud(items);
  }, () => undefined);
}

function presentPowerups(items: ActiveSystemItem[]): void {
  activeSystems = items;
  const serial = ++activeSystemsSerial;
  if (perkUi) {
    perkUi.renderActiveHud(items);
    return;
  }
  if (items.length === 0) return;
  void loadPerkUi().then((module) => {
    if (serial === activeSystemsSerial) module.renderActiveHud(items);
  }, () => undefined);
}

function presentBuild(items: PerkBuildEntry[]): void {
  const serial = ++installedBuildSerial;
  const render = (module: typeof import('./perks')): void => {
    if (serial === installedBuildSerial) module.renderBuildHud(items);
  };
  if (perkUi) {
    render(perkUi);
    return;
  }
  // An empty build is the authored first-paint state. Avoid loading the whole
  // draft module merely to clear a HUD row that is already empty.
  if (items.length === 0) {
    const host = document.getElementById('build-hud')!;
    host.replaceChildren();
    host.hidden = true;
    host.setAttribute('aria-label', 'No upgrades installed');
    return;
  }
  void loadPerkUi().then(render, () => undefined);
}

/* ---------------- viewport fit (pallasite pattern) ---------------- */

function fit(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  world.renderer.setSize(w, h, false);
  world.camera.aspect = w / h;
  world.camera.updateProjectionMatrix();
}

function sampleRenderQuality(frameMs: number, active: boolean): number | null {
  const ratio = renderQuality.observe(frameMs, active);
  if (ratio === null) return null;
  world.renderer.setPixelRatio(ratio);
  fit();
  return ratio;
}

let refitTimers: ReturnType<typeof setTimeout>[] = [];
function scheduleRefit(): void {
  // iOS reports stale dimensions for a beat after rotation; land on settled dims.
  for (const t of refitTimers) clearTimeout(t);
  refitTimers = [120, 280, 480, 650].map((ms) => setTimeout(fit, ms));
  requestAnimationFrame(fit);
}

window.addEventListener('resize', scheduleRefit);
window.addEventListener('orientationchange', scheduleRefit);
window.visualViewport?.addEventListener('resize', scheduleRefit);
window.addEventListener('pageshow', scheduleRefit);
fit();

/* ---------------- game loop ---------------- */

const FIXED_STEP = 1 / 60;
const MAX_CATCHUP = 3;
let lastFrame = performance.now();
let accumulator = 0;

// The title typeface is part of the artwork. Keep the branded boot card up
// until the three tiny local weights are ready, so a cold connection cannot
// reveal the menu in a fallback face and visibly restyle it a moment later.
let bootTypographyReady = false;
const finishBootTypography = (): void => { bootTypographyReady = true; };
const bootTypographyFallback = setTimeout(finishBootTypography, 5000);
void Promise.all([
  document.fonts.load('500 16px "Chakra Petch"'),
  document.fonts.load('600 16px "Chakra Petch"'),
  document.fonts.load('700 16px "Chakra Petch"'),
]).then(() => {
  clearTimeout(bootTypographyFallback);
  finishBootTypography();
}).catch(() => {
  clearTimeout(bootTypographyFallback);
  finishBootTypography();
});

function loop(now: number): void {
  const frameMs = now - lastFrame;
  const deltaS = Math.min(frameMs / 1000, MAX_CATCHUP * FIXED_STEP);
  lastFrame = now;
  if (!document.hidden) {
    pollGamepad();
    game.advancePresentationTime(frameMs);
    accumulator += deltaS;
    let steps = 0;
    while (accumulator >= FIXED_STEP && steps < MAX_CATCHUP) {
      game.update(FIXED_STEP);
      accumulator -= FIXED_STEP;
      steps++;
    }
    if (steps === MAX_CATCHUP) accumulator = 0;
    sampleRenderQuality(frameMs,
      game.phase === 'playing' && !game.paused && !game.userPaused);
    world.renderer.render(world.scene, world.camera);
    // First frame on the glass: the world is behind the menu now, so the menu
    // can come up over a finished picture rather than over bare markup.
    if (bootTypographyReady && document.documentElement.classList.contains('booting')) {
      document.documentElement.classList.remove('booting');
    }
  }
  requestAnimationFrame(loop);
}
requestAnimationFrame(loop);

function pauseForInterruption(): void {
  // A phone lock, app switch, background tab or desktop focus loss must never
  // leave the simulation live while the player's controls are being released.
  if (game.phase === 'playing' && !game.demoMode && !game.paused &&
    (!game.userPaused || resumeCountdownActive)) {
    setPaused(true);
  }
}

window.addEventListener('blur', pauseForInterruption);
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    pauseForInterruption();
    suspendAudio();
  } else {
    lastFrame = performance.now();
    // suspendAudio pauses the current HTML music element on every screen, not
    // just in play, so restore the title/pause track as well as biome music.
    resumeAudio(true);
  }
});

/* ---------------- UI wiring ---------------- */

initInput();

// Title music can only start after a user gesture; catch the first one.
// Registered before menu nav so its capture handler sees the first key
// even when the nav consumes it.
let gestured = false;
let debriefReady: Promise<typeof import('./debrief')> | null = null;
let preloadRuntime: Promise<typeof import('./preload')> | null = null;
function firstGesture(): void {
  if (gestured) return;
  gestured = true;
  unlockAudio();
  startAmbience();
  if (game.phase !== 'playing') setMusicTrack('title');
  primeMusicTrack(BIOME_TRACK[startingLand()]);
  // Warm the boot loader while the player reads the menu.
  void (preloadRuntime ??= import('./preload'))
    .then(({ ensureLoaded }) => ensureLoaded(startingLand()));
  void import('./prophecy').then((module) => module.startProphecyTicker(), () => undefined);
  debriefReady = import('./debrief');
  void debriefReady.then(({ createScoreChase }) => {
    scoreChase ??= createScoreChase(document.getElementById('score-chase')!, sfxScoreRecord);
    scoreChase.reset(game.phase === 'playing' && !game.demoMode ? game.score : 0);
  }, () => { debriefReady = null; });
}
window.addEventListener('pointerdown', firstGesture, { once: true });
window.addEventListener('keydown', firstGesture, { once: true, capture: true });
// If a browser rejects the post-loader bed switch, the very next control press
// is another authorised chance to restore it. This is a no-op while it plays.
window.addEventListener('pointerdown', nudgeMusic);
window.addEventListener('keydown', nudgeMusic);

initMenuNav();
initGamepad({
  onConnect: () => {
    beamControl = 'A';
    document.getElementById('controls-hint')!.textContent =
      '🎮 Gamepad — stick to fly · A to beam · X/LB special · Start to pause · d-pad + A in menus';
    if (game.phase === 'playing') hud.banner('🎮 GAMEPAD CONNECTED');
  },
  onPauseToggle: () => {
    if (game.demoMode) exitDemo();
    else togglePause();
  },
  onActivity: () => {
    firstGesture();
    if (game.demoMode) exitDemo();
    else armAttract();
  },
});

document.getElementById('controls-hint')!.textContent = coarse
  ? 'Left pad to fly · hold BEAM to abduct · tap SPECIAL to deploy a stored item'
  : 'WASD / arrows to fly · hold SPACE to beam · SHIFT deploys a stored special';

const loginBtn = document.getElementById('nostr-login')!;
const callsignBtn = document.getElementById('callsign')!;
const goCallsignBtn = document.getElementById('go-callsign')!;
const signinStatus = document.getElementById('signin-status')!;

function refreshIdentityUi(): void {
  const session = currentAuth()?.getSession() ?? null;
  if (session) {
    const label = session.displayName || `${session.pubkey.slice(0, 8)}…`;
    signinStatus.textContent = `Signed in as ${label} — scores publish under your npub`;
    loginBtn.textContent = 'SIGN OUT';
    callsignBtn.hidden = true;
    goCallsignBtn.hidden = true;
  } else {
    const name = getGuestName();
    signinStatus.textContent = window.nostr
      ? 'NIP-07 signer detected — scores publish under your npub'
      : name
        ? `Guest: ${name} — scores signed with a local key`
        : 'Guest mode — scores signed with a local key';
    loginBtn.textContent = 'SIGN IN WITH NOSTR';
    callsignBtn.hidden = false;
    goCallsignBtn.hidden = !!window.nostr || !!name;
  }
}

loginBtn.addEventListener('click', () => {
  unlockAudio();
  loginBtn.textContent = '…';
  const finished = (): void => {
    refreshIdentityUi();
    void refreshOutboxStatus();
    void retryOutbox();
  };
  void loadAuth().then(async (auth) => {
    if (auth.getSession()) await auth.logoutSignet();
    else await auth.loginWithSignet();
  }).then(finished, finished);
});

async function editCallsign(): Promise<void> {
  // Most flights never open the optional QWERTY panel. Keep its DOM builder
  // out of the first-load bundle and fetch it only from this explicit action.
  const { openNameEntry } = await import('./keypad');
  openNameEntry(getGuestName(), (name) => {
    setGuestName(name);
    void loadAuth().then((auth) => auth.publishGuestProfile(WRITE_RELAYS), () => undefined);
    refreshIdentityUi();
  });
}

callsignBtn.addEventListener('click', () => void editCallsign());
goCallsignBtn.addEventListener('click', () => void editCallsign());

const outboxButton = document.getElementById('score-outbox') as HTMLButtonElement;
let retryingOutbox = false;

async function refreshOutboxStatus(): Promise<void> {
  let pending = 0;
  try {
    pending = (await loadScoring()).pendingScoreCount();
  } catch {
    // Keep a previously visible retry affordance intact when the optional
    // chunk is temporarily unavailable; otherwise the hidden default is safe.
    return;
  }
  outboxButton.hidden = pending === 0;
  outboxButton.disabled = retryingOutbox;
  if (pending > 0 && !retryingOutbox) {
    outboxButton.textContent = `📡 ${pending} SCORE${pending === 1 ? '' : 'S'} QUEUED ON THIS DEVICE · RETRY`;
  }
}

async function retryOutbox(): Promise<void> {
  // Never open a signer or spend network time in the middle of live play.
  if (retryingOutbox || game.phase === 'playing') return;
  let scoring: typeof import('./scoring');
  try {
    scoring = await loadScoring();
  } catch {
    return;
  }
  // The player can launch while this optional chunk is in flight.
  if (String(game.phase) === 'playing') return;
  if (scoring.pendingScoreCount() === 0) {
    void refreshOutboxStatus();
    return;
  }
  retryingOutbox = true;
  outboxButton.hidden = false;
  outboxButton.disabled = true;
  outboxButton.textContent = '📡 RETRYING QUEUED SCORES…';
  try {
    await scoring.retryQueuedScores();
  } finally {
    retryingOutbox = false;
    void refreshOutboxStatus();
  }
}

outboxButton.addEventListener('click', () => void retryOutbox());
refreshIdentityUi();
void refreshOutboxStatus();
if (getIdentityMode() === 'nostr') {
  void loadAuth().then((auth) => auth.restoreIdentity()).then(() => {
    refreshIdentityUi();
    void retryOutbox();
  }, () => undefined);
} else void retryOutbox();
let v4v: typeof import('./v4v') | null = null;
async function loadV4v(): Promise<typeof import('./v4v')> {
  if (v4v) return v4v;
  v4v = await import('./v4v');
  v4v.initV4v();
  return v4v;
}

function openAudioSettings(): void {
  void import('./audio-mixer').then((module) => module.openAudioMixer(), () => undefined);
}

document.getElementById('mute')!.addEventListener('click', openAudioSettings);
document.getElementById('pause-mute')!.addEventListener('click', openAudioSettings);

function startRun(): void {
  flightCardSerial++;
  flightCardFile = null;
  publishAttempt++; // a late response from the previous report cannot repaint the next one
  cancelResumeCountdown();
  unlockAudio();
  startAmbience();
  startMusic();
  void loadPerkUi().catch(() => { perkUiReady = null; });
  overlay.menu.hidden = true;
  overlay.gameover.hidden = true;
  pausedOverlay.hidden = true;
  hud.showHud(true);
  setGameplayControls(true);
  dailyHudArmed = false;
  document.getElementById('daily-hud')!.hidden = true;
  document.getElementById('mobile-daily')!.hidden = true;
  game.blessed = v4v?.isBlessed() ?? false; // first flight loads the V4V gate before reaching here
  scoreChase?.reset();
  game.start(startingLand()); // the game announces the biome itself: title card + voice line
  // Entity-specific voices enter the paced queue first; the broad late-wave
  // catalogue follows once the opening herd and council have claimed priority.
  Object.values(VOICE).forEach((voice) => preloadVoiceClip(voice));
}

/* ---------------- boot loader ---------------- */
// Everything a run touches — portraits, announcer lines, music beds — loads
// behind this bar. Warmed in the background from the first gesture, so on a
// good connection it never appears at all.

const LOAD_TIPS = [
  'Polishing beam…',
  'Warming the choir…',
  'Hiding leaders…',
];

async function loadThenStart(): Promise<void> {
  // This function enters from PLAY, NEXT TIME, START THE HARVEST or GO AGAIN.
  // Prime before the first await while WebKit still recognises that gesture.
  primeMusicTrack(BIOME_TRACK[startingLand()]);
  const loadingEl = document.getElementById('loading')!;
  const fill = document.getElementById('load-fill')!;
  const pct = document.getElementById('load-pct')!;
  const tip = document.getElementById('load-tip')!;
  let shown = false;
  let tipTimer: ReturnType<typeof setInterval> | null = null;
  // Only surface the bar if loading actually takes a moment.
  const showAfter = setTimeout(() => {
    shown = true;
    overlay.menu.hidden = true;
    loadingEl.hidden = false;
    let i = Math.floor(Math.random() * LOAD_TIPS.length);
    tip.textContent = LOAD_TIPS[i];
    tipTimer = setInterval(() => {
      i = (i + 1) % LOAD_TIPS.length;
      tip.textContent = LOAD_TIPS[i];
    }, 1500);
  }, 150);
  await Promise.all([
    (preloadRuntime ??= import('./preload')).then(({ ensureLoaded }) =>
      ensureLoaded(startingLand(), (done, total) => {
        const p = Math.round((done / total) * 100);
        fill.style.width = `${p}%`;
        pct.textContent = `${p}%`;
      })),
    game.warmGameplay(),
  ]);
  clearTimeout(showAfter);
  if (tipTimer) clearInterval(tipTimer);
  startRun();
  // Upload the newly spawned herd, buildings and detailed cast while the load
  // gate is still on glass. The next RAF is then play, not shader preparation.
  world.renderer.render(world.scene, world.camera);
  if (shown) loadingEl.hidden = true;
}

async function startFlow(): Promise<void> {
  unlockAudio();
  overlay.menu.hidden = true;
  const gate = await loadV4v();
  if (gate.shouldAskV4v()) gate.openV4v(() => void loadThenStart());
  else void loadThenStart();
}

document.getElementById('play')!.addEventListener('click', () => void startFlow());
document.getElementById('retry')!.addEventListener('click', () => void loadThenStart());
const shareButton = document.getElementById('share') as HTMLButtonElement;
shareButton.addEventListener('click', () => {
  const url = 'https://ledger.playechoseven.com/';
  if (navigator.share) {
    const data: ShareData = { title: 'Take Me To Your Ledger', text: flightReceipt, url };
    if (flightCardFile && typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [flightCardFile] })) data.files = [flightCardFile];
    // The card is prepared before the tap so iOS keeps this inside the trusted
    // share gesture. A very early tap still gets the established text receipt.
    void navigator.share(data).catch(() => undefined);
  } else {
    if (flightCardFile) {
      const cardUrl = URL.createObjectURL(flightCardFile);
      const anchor = document.createElement('a');
      anchor.href = cardUrl;
      anchor.download = flightCardFile.name;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(cardUrl), 1_000);
    }
    void navigator.clipboard?.writeText(`${flightReceipt} ${url}`).then(() => {
      shareButton.textContent = flightCardFile ? 'IMAGE SAVED!' : 'COPIED!';
      setTimeout(() => { shareButton.textContent = 'SHARE FIELD'; }, 1400);
    }).catch(() => undefined);
  }
});
function returnToMenu(): void {
  // Game-over reaches this path without the pause menu's explicit abandon.
  // Clear biome-owned actors before repainting the preview as the opener.
  game.abandonRun();
  flightCardSerial++;
  flightCardFile = null;
  cancelResumeCountdown();
  overlay.gameover.hidden = true;
  pausedOverlay.hidden = true;
  overlay.menu.hidden = false;
  hud.showHud(false);
  hud.hideBiomeCard();
  setGameplayControls(false);
  setMusicTrack('title');
  world.applyBiome(startingLand()); // tour runs end anywhere; preview the next opener
  setAmbientBiome(world.biome);
  void refreshOutboxStatus(); // also expires yesterday's queue at the UTC boundary
  void refreshMenuLive();
  armAttract();
}

document.getElementById('to-menu')!.addEventListener('click', () => {
  returnToMenu();
});

const publishStatus = document.getElementById('publish-status')!;
const publishRetry = document.getElementById('publish-retry') as HTMLButtonElement;
let publishAttempt = 0;

async function publishFinishedRun(run: RunResult): Promise<void> {
  const attempt = ++publishAttempt;
  let accepted = false;
  publishRetry.hidden = true;
  publishRetry.disabled = true;
  publishStatus.className = 'publish-status';
  publishStatus.textContent = 'Signing score & publishing to Nostr…';
  try {
    const outcome = await (await loadScoring()).publishScore(run);
    if (lastRun !== run || attempt !== publishAttempt) return;
    if (outcome.ok) {
      accepted = true;
      publishStatus.textContent = 'Score verified & accepted by game server';
      publishStatus.className = 'publish-status ok';
    } else if (outcome.skipped) {
      // Automation must never touch the live claim service; keep its visual
      // artifacts neutral instead of pretending the network failed.
      publishStatus.textContent = 'Test flight — leaderboard publishing skipped';
    } else if (outcome.rejected) {
      publishStatus.textContent = 'Score claim rejected — personal best kept locally';
      publishStatus.className = 'publish-status err';
    } else {
      publishStatus.textContent = outcome.queued
        ? 'Score queued until midnight UTC'
        : 'Leaderboard offline — tap retry';
      publishStatus.className = outcome.queued ? 'publish-status queued' : 'publish-status err';
      publishRetry.hidden = false;
      publishRetry.disabled = false;
    }
  } catch {
    if (lastRun !== run || attempt !== publishAttempt) return;
    publishStatus.textContent = 'Leaderboard unavailable — tap retry';
    publishStatus.className = 'publish-status err';
    publishRetry.hidden = false;
    publishRetry.disabled = false;
  } finally {
    void refreshOutboxStatus();
    if (accepted) void retryOutbox();
  }
}

publishRetry.addEventListener('click', () => {
  if (!lastRun || overlay.gameover.hidden) return;
  void publishFinishedRun(lastRun);
});

window.addEventListener('online', () => {
  if (game.phase === 'playing') return;
  if (lastRun && !overlay.gameover.hidden && !publishRetry.hidden) {
    void publishFinishedRun(lastRun);
  } else {
    void retryOutbox();
  }
});

async function showGameOver(): Promise<void> {
  hud.showHud(false);
  hud.hideBiomeCard();
  const debrief = await (debriefReady ??= import('./debrief'));
  const detailNode = document.getElementById('go-detail')!;
  const report = debrief.renderRunReport(
    detailNode, game, game.tourComplete, game.extracted, game.lastDamageSource,
  );
  setMusicTrack(report.music);
  overlay.gameover.dataset.outcome = report.outcome;
  document.getElementById('go-kicker')!.textContent = report.kicker;
  document.getElementById('go-title')!.textContent = report.title;
  document.getElementById('go-next-kicker')!.textContent = report.nextKicker;
  document.getElementById('retry')!.textContent = report.retry;
  const score = game.score;
  lastRun = {
    score,
    wave: game.wave,
    cows: game.cowsAbducted,
    cultists: game.cultistsAbducted,
    groupies: game.groupiesAbducted,
    drones: game.dronesAbducted,
    normies: game.normiesAbducted,
    cropBonus: game.cropBonus,
    bestCombo: game.bestCombo,
    council: game.councilAwarded,
    durationMs: game.durationMs,
    runId: game.runId,
    awards: [...game.runAwards],
    stage: game.stage,
    land: world.biome,
  };
  document.getElementById('go-score')!.textContent = score.toLocaleString('en-GB');
  const elapsedSeconds = Math.max(0, Math.floor(lastRun.durationMs / 1000));
  const flightTime = `${Math.floor(elapsedSeconds / 60)}:${String(elapsedSeconds % 60).padStart(2, '0')}`;
  document.getElementById('go-animals')!.textContent = String(lastRun.cows);
  document.getElementById('go-cultists')!.textContent = String(lastRun.cultists);
  document.getElementById('go-groupies')!.textContent = String(lastRun.groupies);
  document.getElementById('go-wave')!.textContent = String(lastRun.wave);
  document.getElementById('go-lands')!.textContent = String(game.stage);
  document.getElementById('go-chain')!.textContent = `x${lastRun.bestCombo}`;
  document.getElementById('go-time')!.textContent = flightTime;
  debrief.renderFlightHighlights(document.getElementById('go-feats')!, game);
  flightReceipt = report.shareText;
  const cardRun = lastRun;
  const cardSerial = ++flightCardSerial;
  flightCardFile = null;
  shareButton.textContent = 'SHARE FIELD';
  shareButton.dataset.image = 'preparing';
  shareButton.setAttribute('aria-label', 'Share flight score');
  // The share image is a post-run-only lazy chunk. It draws the real stalk
  // state into a deterministic receipt rather than reading pixels back from
  // WebGL, avoiding a GPU stall and working consistently on iOS Safari.
  void import('./flight-card').then(({ createFlightCardFile }) =>
    createFlightCardFile(cardRun, world.corn.shareSnapshot(), LAND_HUD_LABEL[world.biome]))
    .then((file) => {
      if (cardSerial !== flightCardSerial || lastRun !== cardRun || !file) return;
      flightCardFile = file;
      shareButton.dataset.image = 'ready';
      shareButton.setAttribute('aria-label', 'Share flight score and crop field image');
    }).catch(() => {
      if (cardSerial === flightCardSerial) delete shareButton.dataset.image;
    });
  if (report.records.improved.includes('score')) playVoiceClip(VOICE.allTimeHigh, 1);
  else if (report.gameOverVoice) playVoiceClip(VOICE.ttsGameover, 0.85);
  const awardsNode = document.getElementById('go-awards')!;
  awardsNode.hidden = lastRun.awards.length === 0;
  awardsNode.textContent = '';
  if (lastRun.awards.length > 0) {
    const badges = lastRun.awards
      .map((id) => awardState().find((a) => a.def.id === id)?.def)
      .filter(Boolean)
      .map((d) => `${d!.emoji} ${d!.name}`)
      .join(' · ');
    awardsNode.textContent = `NEW · ${badges}`;
  }
  overlay.gameover.hidden = false;

  publishAttempt++;
  publishStatus.className = 'publish-status';
  publishRetry.hidden = true;
  publishRetry.disabled = false;
  if (score <= 0) {
    publishStatus.textContent = '';
    return;
  }
  const run = lastRun;
  void publishFinishedRun(run);
}

/* ---------------- leaderboard ---------------- */

const boardList = document.getElementById('board-list')!;
let boardMode: 'all' | 'today' | 'season' = 'all';
let boardRequest = 0;

function boardItem(row: BoardRow, index: number, me: string | null, showFlight = false, crowns = 0): HTMLLIElement {
  const li = document.createElement('li');
  if (row.pubkey === me) li.className = 'me';
  const rank = document.createElement('span');
  rank.className = 'rank';
  rank.textContent = `${index + 1}.`;
  const pilot = document.createElement('span');
  pilot.className = 'pilot';
  const name = document.createElement('span');
  name.className = 'name';
  name.textContent = row.name || `${row.pubkey.slice(0, 8)}…`;
  if (crowns > 0) {
    const crown = document.createElement('span');
    crown.className = 'crowns';
    crown.textContent = crowns > 3 ? `👑×${crowns}` : '👑'.repeat(crowns);
    crown.title = `${crowns} daily crown${crowns === 1 ? '' : 's'} this epoch`;
    name.append(' ', crown);
  }
  pilot.append(name);
  if (showFlight && row.wave) {
    const flight = document.createElement('span');
    flight.className = 'flight';
    flight.textContent = `WAVE ${row.wave}` +
      (row.stage ? ` · ${row.stage} LAND${row.stage === 1 ? '' : 'S'}` : '');
    pilot.append(flight);
  }
  const pts = document.createElement('span');
  pts.className = 'pts';
  pts.textContent = row.score.toLocaleString('en-GB');
  li.append(rank, pilot, pts);
  return li;
}

async function openBoard(): Promise<void> {
  overlay.board.hidden = false;
  const request = ++boardRequest; // stale responses must not clobber a newer tab
  for (const mode of ['all', 'today', 'season'] as const) {
    document.getElementById(`board-tab-${mode}`)!.className = boardMode === mode ? 'board-tab active' : 'board-tab';
  }
  const meta = document.getElementById('board-season-meta')!;
  meta.hidden = boardMode !== 'season';
  if (boardMode === 'season') {
    const season = seasonInfo();
    meta.textContent = `${season.label} · ${season.daysLeft} day${season.daysLeft === 1 ? '' : 's'} left · 👑 = daily crowns won`;
  }
  boardList.innerHTML = '<li class="dim">Contacting relays…</li>';
  try {
    const scoring = await loadScoring();
    const rows = boardMode === 'season'
      ? await scoring.loadSeasonBoard(10)
      : await scoring.loadLeaderboard(10, boardMode === 'today' ? scoring.todayStart() : undefined);
    if (request !== boardRequest) return;
    if (rows.length === 0) {
      boardList.innerHTML = boardMode === 'today'
        ? '<li class="dim">No harvests today — the daily crown is unclaimed</li>'
        : boardMode === 'season'
          ? '<li class="dim">No harvests this epoch yet — the season is wide open</li>'
          : '<li class="dim">No scores yet — be the first abductor</li>';
      return;
    }
    const me = scoring.peekPubkey();
    boardList.innerHTML = '';
    rows.forEach((row, i) => {
      const crowns = (row as SeasonRow).crowns ?? 0;
      boardList.append(boardItem(row, i, me, true, boardMode === 'season' ? crowns : 0));
    });
  } catch {
    if (request === boardRequest) boardList.innerHTML = '<li class="dim">Relays unreachable</li>';
  }
}

document.getElementById('show-board')!.addEventListener('click', () => void openBoard());
document.getElementById('daily-contract')!.addEventListener('click', () => {
  boardMode = 'today';
  void openBoard();
});
document.getElementById('board-close')!.addEventListener('click', () => {
  overlay.board.hidden = true;
});
for (const mode of ['all', 'today', 'season'] as const) {
  document.getElementById(`board-tab-${mode}`)!.addEventListener('click', () => {
    boardMode = mode;
    void openBoard();
  });
}

/* ---------------- pause ---------------- */

const pausedOverlay = document.getElementById('paused')!;
const pauseEndButton = document.getElementById('pause-menu') as HTMLButtonElement;
const pauseHint = document.getElementById('pause-hint')!;
const resumingOverlay = document.getElementById('resuming')!;
const resumeCount = document.getElementById('resume-count')!;
const RESUME_STEP_MS = 650;
let resumeCountdownActive = false;
let resumeCountdownNonce = 0;
let resumeCountdownTimers: ReturnType<typeof setTimeout>[] = [];

function cancelResumeCountdown(): void {
  resumeCountdownNonce++;
  resumeCountdownActive = false;
  for (const timer of resumeCountdownTimers) clearTimeout(timer);
  resumeCountdownTimers = [];
  resumingOverlay.hidden = true;
  resumingOverlay.dataset.stage = '';
}

function beginResumeCountdown(): void {
  if (game.phase !== 'playing' || !game.userPaused || resumeCountdownActive) return;
  cancelResumeCountdown();
  const nonce = resumeCountdownNonce;
  resumeCountdownActive = true;
  pausedOverlay.hidden = true;
  resumingOverlay.hidden = false;
  resumingOverlay.dataset.stage = 'count';
  resumeCount.textContent = '3';
  // Controls come back before the simulation does: touch players can replace
  // their thumbs and every input can be buffered safely behind the freeze.
  setGameplayControls(true);

  for (const [index, label] of ['2', '1'].entries()) {
    resumeCountdownTimers.push(setTimeout(() => {
      if (nonce !== resumeCountdownNonce || game.phase !== 'playing') return;
      resumeCount.textContent = label;
    }, RESUME_STEP_MS * (index + 1)));
  }
  resumeCountdownTimers.push(setTimeout(() => {
    if (nonce !== resumeCountdownNonce || game.phase !== 'playing') return;
    resumeCountdownActive = false;
    resumingOverlay.dataset.stage = 'fly';
    resumeCount.textContent = 'FLY';
    game.setUserPaused(false);
  }, RESUME_STEP_MS * 3));
  resumeCountdownTimers.push(setTimeout(() => {
    if (nonce !== resumeCountdownNonce) return;
    resumingOverlay.hidden = true;
    resumingOverlay.dataset.stage = '';
    resumeCountdownTimers = [];
  }, RESUME_STEP_MS * 3 + 420));
}

function setPaused(on: boolean): void {
  // Zero energy is already a short, real-time cinematic. Keyboard, gamepad or
  // focus loss must not cover the wreck with a pause panel the player cannot
  // meaningfully resume.
  if (game.phase !== 'playing' || game.hull <= 0) return;
  pauseEndButton.dataset.confirm = '';
  pauseEndButton.textContent = pauseEndButton.dataset.default!;
  pauseEndButton.setAttribute('aria-label', pauseEndButton.dataset.default!);
  pauseHint.textContent = pauseHint.dataset.default!;
  if (on) {
    cancelResumeCountdown();
    game.setUserPaused(true);
    pausedOverlay.hidden = false;
    setGameplayControls(false);
    const renderLoadout = (module: typeof import('./perks')): void => {
      module.renderPerkBuild(document.getElementById('pause-build')!, game.perkBuild());
      module.renderFlightSystems(document.getElementById('pause-special')!, specialReserve, activeSystems);
    };
    if (perkUi) renderLoadout(perkUi);
    else void loadPerkUi().then(renderLoadout, () => undefined);
  } else {
    beginResumeCountdown();
  }
}

function togglePause(): void {
  if (resumeCountdownActive || !resumingOverlay.hidden) setPaused(true);
  else setPaused(!game.userPaused);
}

document.getElementById('pause-btn')!.addEventListener('click', () => setPaused(true));
document.getElementById('resume')!.addEventListener('click', () => setPaused(false));
pauseEndButton.addEventListener('click', async () => {
  if (pauseEndButton.dataset.confirm !== '1') {
    const debrief = await (debriefReady ??= import('./debrief'));
    debrief.armExtractionPrompt(pauseEndButton, pauseHint, game.score);
    return;
  }
  pausedOverlay.hidden = true;
  if (!game.extractRun()) {
    pausedOverlay.hidden = false;
    setPaused(true);
  }
});
window.addEventListener('keydown', (e) => {
  if ((e.code === 'KeyP' || e.code === 'Escape') && game.phase === 'playing' && !game.paused) {
    togglePause();
  }
});

/* ---------------- badges ---------------- */

function openBadges(): void {
  const grid = document.getElementById('badge-grid')!;
  grid.innerHTML = '';
  for (const { def, unlockedAt } of awardState()) {
    const cell = document.createElement('div');
    cell.className = unlockedAt ? 'badge' : 'badge locked';
    const emoji = document.createElement('div');
    emoji.className = 'b-emoji';
    emoji.textContent = def.emoji;
    const txt = document.createElement('div');
    const name = document.createElement('div');
    name.className = 'b-name';
    name.textContent = def.name;
    const desc = document.createElement('div');
    desc.className = 'b-desc';
    desc.textContent = def.desc;
    txt.append(name, desc);
    cell.append(emoji, txt);
    grid.append(cell);
  }
  document.getElementById('badges')!.hidden = false;
}

document.getElementById('show-badges')!.addEventListener('click', openBadges);
document.getElementById('badges-close')!.addEventListener('click', () => {
  document.getElementById('badges')!.hidden = true;
});

/* ---------------- the congregation ---------------- */

function openCongregation(): void {
  const grid = document.getElementById('souls-grid')!;
  grid.innerHTML = '';
  for (const { member, count } of congregationState()) {
    const cell = document.createElement('div');
    cell.className = count > 0 ? 'soul' : 'soul locked';
    const img = document.createElement('img');
    img.src = `/cult/${member.id}.webp`;
    img.alt = count > 0 ? member.name : 'Ungathered soul';
    img.loading = 'lazy';
    cell.append(img);
    if (count > 1) {
      const n = document.createElement('div');
      n.className = 's-count';
      n.textContent = `×${count}`;
      cell.append(n);
    }
    const name = document.createElement('div');
    name.className = 's-name';
    name.textContent = count > 0 ? member.name : '???';
    cell.append(name);
    if (count > 0) cell.title = `${member.name} — ${member.role}. ${member.tagline}`;
    grid.append(cell);
  }
  document.getElementById('souls-progress')!.textContent =
    `${gatheredCount()} / ${ROSTER.length} SOULS GATHERED`;
  document.getElementById('congregation')!.hidden = false;
}

document.getElementById('show-souls')!.addEventListener('click', openCongregation);
document.getElementById('souls-close')!.addEventListener('click', () => {
  document.getElementById('congregation')!.hidden = true;
});

/* ---------------- live menu widgets ---------------- */
// The board and badge shelf live ON the menu — no click needed to see them.

const menuLive = document.getElementById('menu-live')!;
const menuBoardList = document.getElementById('menu-board-list')!;
let menuLiveAt = 0;

async function refreshMenuLive(): Promise<void> {
  renderDaily();
  const unlockedBadges = awardState().filter((a) => a.unlockedAt).length;
  const totalBadges = awardState().length;
  document.getElementById('menu-progress')!.innerHTML =
    `🏅 <b>${unlockedBadges}/${totalBadges}</b> · ⛪ <b>${gatheredCount()}/${ROSTER.length}</b> · ` +
    `🗺 <b>${unlockedLands().length}/${LAND_ORDER.length}</b> TOUR`;
  menuLive.hidden = false;
  if (Date.now() - menuLiveAt < 60000) return; // relay courtesy: refresh at most per minute
  menuLiveAt = Date.now();
  menuBoardList.innerHTML = '<li class="dim">contacting relays…</li>';
  try {
    const scoring = await loadScoring();
    const rows = await scoring.loadLeaderboard(3);
    if (rows.length === 0) {
      menuBoardList.innerHTML = '<li class="dim">no harvests yet — be first</li>';
      return;
    }
    const me = scoring.peekPubkey();
    menuBoardList.innerHTML = '';
    rows.forEach((row, i) => {
      menuBoardList.append(boardItem(row, i, me));
    });
  } catch {
    menuBoardList.innerHTML = '<li class="dim">relays unreachable</li>';
  }
}

document.getElementById('menu-board')!.addEventListener('click', () => void openBoard());
void refreshMenuLive();

/* ---------------- attract mode (cabinet demo) ---------------- */
// Idle on the menu long enough and the saucer flies itself, arcade-style.
// Any input hands the controls back.

const demoBanner = document.getElementById('demo-banner')!;
const DEMO_IDLE_MS = 30000;
const DEMO_LENGTH_MS = 55000;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let demoTimer: ReturnType<typeof setTimeout> | null = null;
// A fresh page has not exited a demo; do not swallow its first title tap.
let demoExitAt = -450;

function armAttract(): void {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    if (game.phase === 'idle' && !overlay.menu.hidden && !document.hidden) enterDemo();
    else armAttract();
  }, DEMO_IDLE_MS);
}

function enterDemo(): void {
  setGameplayControls(false);
  overlay.menu.hidden = true;
  hud.showHud(true);
  document.getElementById('daily-hud')!.hidden = true;
  document.getElementById('mobile-daily')!.hidden = true;
  demoBanner.hidden = false;
  game.startDemo();
  if (demoTimer) clearTimeout(demoTimer);
  demoTimer = setTimeout(exitDemo, DEMO_LENGTH_MS);
}

function exitDemo(): void {
  if (!game.demoMode) return;
  if (demoTimer) clearTimeout(demoTimer);
  demoTimer = null;
  demoExitAt = performance.now();
  game.abortDemo();
  demoBanner.hidden = true;
  hud.showHud(false);
  setGameplayControls(false);
  overlay.menu.hidden = false;
  setMusicTrack('title');
  world.applyBiome(startingLand()); // the demo may have toured; restore the preview
  setAmbientBiome(world.biome);
  void refreshMenuLive();
  armAttract();
}

window.addEventListener('pointerdown', (e) => {
  if (game.demoMode) {
    e.preventDefault();
    e.stopImmediatePropagation();
    exitDemo();
  } else {
    armAttract();
  }
}, true);
// Swallow the click that follows a demo-exit press so it cannot hit PLAY.
window.addEventListener('click', (e) => {
  if (performance.now() - demoExitAt < 450) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}, true);
window.addEventListener('keydown', (e) => {
  if (game.demoMode) {
    e.preventDefault();
    e.stopImmediatePropagation();
    exitDemo();
  } else {
    armAttract();
  }
}, true);
armAttract();

// Dev panel: opt in with ?dev. Dynamic import so Vite splits it into its own
// chunk and players never download it.
if (new URLSearchParams(location.search).has('dev')) {
  void import('./dev').then((m) => m.initDevPanel(game, world)).catch(() => undefined);
}

// ?voices — the animal call A/B screen. Split out the same way the dev panel is.
if (new URLSearchParams(location.search).has('voices')) {
  void import('./voice-lab').then((m) => m.initVoiceLab()).catch(() => undefined);
}

// Visible build tag: instantly answers "which version is this device running?"
// (stale caches once hid a whole feature drop).
document.getElementById('build-tag')!.textContent = `v${__APP_VERSION__} · ${__BUILD_ID__}`;

// Dev/testing handle (compile-time privates are reachable at runtime by design).
(window as unknown as Record<string, unknown>).__tmtyl = {
  game,
  world,
  quality: {
    sample: sampleRenderQuality,
    snapshot: () => renderQuality.snapshot(),
  },
  daily: { view: () => dailyRuntime?.dailyView(), update: updateDailyMetric, ready: dailyReady },
  audio: {
    ambience: ambientState,
    call: playAnimalCall,
    // The moo players fall back to when the recording has not arrived.
    synthMoo: sfxCowMoo,
    warmMoo: () => preloadVoiceClipAsync(VOICE.moo),
  },
  input: { state: input, consumeSpecial: consumeSpecialPress },
  hud: { setThreat: hud.setThreat },
  // One dynamic door to THE SIGNAL's whole mark set — ladder, drawings and the
  // stamp measurements — so the glyph profile can rasterise every mark rather
  // than only whichever the local congregation has earned. Dynamic because
  // nothing about it may reach the entry chunk; see glyph-marks.ts.
  glyphs: { load: () => import('./glyph-marks') },
};
